<template>
  <div id="sign-company" class="content-section">
    <div class="section-title">签约企业主体</div>
    <div class="section-content" v-loading="loading">
      <StickyItem title="企业主体：" :content="modelName" />
      <div class="car-model-info">
        <!-- 数据展示 -->
        <div class="info-list">
          <div
            v-for="(item, index) in filteredInfoItems"
            :key="index"
            class="info-item"
            :class="{ 'full-width': item.fullWidth }"
          >
            <span class="label">{{ item.label }}</span>
            <template v-if="item.type === 'image'">
              <div class="image-wrapper">
                <span
                  class="a-hover-link"
                  v-if="formattedData[item.key]"
                  @click="showImages(formattedData[item.key])"
                  >查看</span
                >
                <span v-else>--</span>
              </div>
            </template>
            <span v-else class="value">{{
              formattedData[item.key] || '--'
            }}</span>
          </div>
        </div>
      </div>
      <StickyItem title="易宝账户：" :content="yeepayStateDesc" />
      <StickyItem title="E签宝：" :content="esignStateDesc" />
      <StickyItem title="合作平台：" :content="cooperationPlatformtDesc" />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { cloneDeep } from 'lodash'
import { use$to } from '@/utils/useUtils'
import StickyItem from './stickyItem.vue'
import { getGoodsEnterpriseInfo } from '@/api/domain/shop'

const $to = use$to()

const props = defineProps({
  enterpriseId: {
    type: [String, Number],
    default: () => '',
  },
  cooperationPlatformNames: {
    type: Array,
    default: () => [],
  },
})

const formData = ref({})
const loading = ref(false)

// 定义信息项配置
const infoItems = computed(() => [
  {
    label: '统一社会信用代码：',
    key: 'licenceNo',
  },
  {
    label: '经营状态：',
    key: 'businessStatusDesc',
  },
  {
    label: '成立日期：',
    key: 'establishDate',
  },
  {
    label: '登记机关：',
    key: 'registrationAuthority',
  },
  {
    label: '经营范围：',
    key: 'businessScope',
  },
  {
    label: '注册电话：',
    key: 'registrationMobile',
  },
  {
    label: '注册地址：',
    key: 'registrationAddress',
  },
  {
    label: '营业执照：',
    key: 'businessLicensePic',
    type: 'image',
    fullWidth: true,
  },
])

// 添加过滤后的信息项计算属性
const filteredInfoItems = computed(() => {
  return infoItems.value.filter((item) => item.show !== false)
})

const modelName = computed(() => {
  const { corporationName } = formData.value
  if (!corporationName) return '--'
  return corporationName
})

// 易宝开通状态  1已开通，0未开通
const yeepayStateDesc = computed(() => {
  if (!props.enterpriseId) return '--'
  if (!formData.value) return ''
  const { yeepayState } = formData.value

  return yeepayState
    ? '已开通'
    : '易宝账户未开通，请联系车务系统技术协助处理'
})
// E签宝开通状态  1已开通，0未开通
const esignStateDesc = computed(() => {
  if (!props.enterpriseId) return '--'
  if (!formData.value) return ''
  const { esignState } = formData.value

  return esignState ? '已授权' : '未完成配置，无法进行线上签约'
})
const cooperationPlatformtDesc = computed(() => {
  if (!props.cooperationPlatformNames?.length) {
    return '--'
  }
  return props.cooperationPlatformNames?.join('、') || '--'
})

// 格式化显示值
const formatValue = (value, unit = '') => {
  if (!value && value !== 0) return '--'
  return unit ? `${value}${unit}` : value
}

const formattedData = computed(() => {
  const baseData = {
    ...formData.value,
  }

  // 遍历 infoItems 为每个字段添加单位
  infoItems.value.forEach((item) => {
    if (item.unit && baseData[item.key] !== undefined) {
      baseData[item.key] = formatValue(baseData[item.key], item.unit)
    }
  })
  if (baseData?.establishDate) {
    baseData.establishDate = baseData.establishDate?.replace('00:00:00', '')
  }

  return baseData
})

const handleGoodsEnterpriseInfo = async () => {
  if (!props.enterpriseId) return
  loading.value = true
  const [err, res] = await $to(
    getGoodsEnterpriseInfo({ id: props.enterpriseId }),
  )
  loading.value = false
  if (err || res?.code !== 1) return
  formData.value = cloneDeep(res?.data)
}

const showImages = (businessLicensePic) => {
  if (!businessLicensePic) return
  window.open(businessLicensePic, '_blank')
}

watch(
  () => props.enterpriseId,
  () => {
    handleGoodsEnterpriseInfo()
  },
)
</script>

<style lang="scss" scoped>
.content-section {
  background: #ffffff;
  border-radius: 12px;
  opacity: 1;
  margin-bottom: 12px;

  .section-title {
    font-size: 16px;
    font-weight: 500;
    color: #333333;
    padding: 8px 16px;
    line-height: 24px;
    letter-spacing: 0em;
    color: rgba(0, 0, 0, 0.9);
  }

  .section-content {
    padding: 0 16px 16px 16px;
  }
  .car-model-info {
    padding: 12px;
    background: #f3f3f4;
    border-radius: 8px;
    opacity: 1;
    min-height: 200px;
    background: #f3f3f4;

    .info-list {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 16px;

      .info-item {
        width: calc(50% - 8px);
        display: flex;
        align-items: flex-start;

        &.full-width {
          width: 100%;
        }

        .label {
          width: auto;
          color: #606266;
          margin-right: 4px;
          flex-shrink: 0;
          text-align: right;
        }

        .value {
          color: #303133;
          flex: 1;
          overflow: hidden;
          word-break: break-all;
          white-space: pre-wrap;
        }

        .image-wrapper {
          width: 160px;
          overflow: hidden;
        }
      }
    }
  }
}
</style>
